import { useContext, createContext, useState, useEffect, useRef } from "react";

export const MeetingAppContext = createContext();

export const useMeetingAppContext = () => useContext(MeetingAppContext);

export const MeetingAppProvider = ({ children }) => {
  // Initialize device selections from localStorage if available
  const [selectedMic, setSelectedMic] = useState(() => {
    try {
      const savedMic = localStorage.getItem('rlink_selectedMic');
      return savedMic ? { id: savedMic, label: null } : { id: null, label: null };
    } catch { return { id: null, label: null }; }
  });
  const [selectedWebcam, setSelectedWebcam] = useState(() => {
    try {
      const savedCamera = localStorage.getItem('rlink_selectedCamera');
      return savedCamera ? { id: savedCamera, label: null } : { id: null, label: null };
    } catch { return { id: null, label: null }; }
  });
  const [selectedSpeaker, setSelectedSpeaker] = useState(() => {
    try {
      const savedSpeaker = localStorage.getItem('rlink_selectedSpeaker');
      return savedSpeaker ? { id: savedSpeaker, label: null } : { id: null, label: null };
    } catch { return { id: null, label: null }; }
  });
  const [isCameraPermissionAllowed, setIsCameraPermissionAllowed] = useState(null);
  const [isMicrophonePermissionAllowed, setIsMicrophonePermissionAllowed] = useState(null);
  const [raisedHandsParticipants, setRaisedHandsParticipants] = useState([]);
  const [brbParticipants, setBrbParticipants] = useState([]);
  const [sideBarMode, setSideBarMode] = useState(null);
  const [pipMode, setPipMode] = useState(false);
  const [unreadChat, setUnreadChat] = useState(false);
  const [recentReactions, setRecentReactions] = useState([]);
  const [participantReactions, setParticipantReactions] = useState({}); // {participantId: {emoji, boosted, ts}}
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const [virtualBackgroundOn, setVirtualBackgroundOn] = useState(false);
  const [blurEnabled, setBlurEnabled] = useState(false);
  const [blurStrength, setBlurStrength] = useState(50);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [participantApproveFunc, setParticipantApproveFunc] = useState([]);
  const [approvalSoundEnabled, setApprovalSoundEnabled] = useState(true);
  const [afterMeetingJoinedHLSState, setAfterMeetingJoinedHLSState] =
    useState(null);
  const [participantWebinarFunc, setParticipantWebinarFunc] = useState([]);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [currentFocusParticipantId, setCurrentFocusParticipantId] = useState(null);

  // Track total speaking time per participant (in seconds)
  // Format: { participantId: totalSpeakingTimeInSeconds }
  const [speakingTimes, setSpeakingTimes] = useState({});
  const speakingStartTimeRef = useRef(null);
  const lastActiveSpeakerRef = useRef(null);

  // Update speaking time when active speaker changes
  useEffect(() => {
    const now = Date.now();

    // If there was a previous speaker, calculate their speaking duration
    if (lastActiveSpeakerRef.current && speakingStartTimeRef.current) {
      const prevSpeakerId = lastActiveSpeakerRef.current;
      const duration = (now - speakingStartTimeRef.current) / 1000; // Convert to seconds

      if (duration > 0.5) { // Only count if spoke for more than 0.5 seconds
        setSpeakingTimes(prev => ({
          ...prev,
          [prevSpeakerId]: (prev[prevSpeakerId] || 0) + duration
        }));
      }
    }

    // Update refs for new speaker
    lastActiveSpeakerRef.current = activeSpeakerId;
    speakingStartTimeRef.current = activeSpeakerId ? now : null;
  }, [activeSpeakerId]);
  const [showRecordingConsentModal, setShowRecordingConsentModal] = useState(false);
  const [isParticipantRowMinimized, setIsParticipantRowMinimized] = useState(false);
  // Shadow banned participants - Set of participant IDs
  // When a user is shadow banned, their NEW messages won't be shown to others
  // Messages sent BEFORE the ban remain visible
  const [shadowBannedParticipants, setShadowBannedParticipants] = useState(new Set());

  // Presenters - Set of participant IDs who have been made presenter by host/co-host
  // Presenters can share their screen (independent from spotlight/pinned state)
  const [presenters, setPresenters] = useState(new Set());

  // ID of the participant who started the current recording (null when not
  // recording). Surfaced as a red "Recording" dot next to that person's name
  // in the participants panel — useful when one of several hosts kicks off a
  // recording and others want to know who owns it. Set in useTopBar via the
  // RECORDING_NOTICE pubsub channel; reset to null on stop.
  const [recordingStartedById, setRecordingStartedById] = useState(null);

  // Grid pagination state shared between ParticipantGrid and StudioBottomControls
  const [gridPagination, setGridPagination] = useState({ currentPage: 0, totalPages: 0 });

  // Participant branding settings - stores branding for each participant
  // Format: { participantId: { showLogo, logoUrl, logoPosition, showQrCode, qrUrl, qrPosition, ... } }
  const [participantBranding, setParticipantBranding] = useState({});

  // Branding settings for overlays (lower third, logo, QR code, video frame)
  const [branding, setBranding] = useState({
    // Colors
    primaryColor: '#6a1fbf',
    accentColor: '#00c853',
    backgroundColor: '#001233',
    // Typography
    font: 'Inter',
    // Video frame
    frame: 'none',
    frameColor: '#6a1fbf',
    frameBorderWidth: 2,
    frameShadow: true,
    gradientColor1: '#6a1fbf',
    gradientColor2: '#00c853',
    // Logo
    logoUrl: '',
    logoPosition: 'top-right',
    showLogo: false,
    // Lower third
    lowerThirdTemplate: 'modern',
    lowerThirdName: '',
    lowerThirdTitle: '',
    showLowerThird: false,
    lowerThirdBackground: true,
    lowerThirdBgColor: '#000000',
    lowerThirdTextColor: '#ffffff',
    lowerThirdTitleColor: '#9ca3af',
    lowerThirdUnderline: false,
    lowerThirdBorder: false,
    lowerThirdBorderColor: '#6a1fbf',
    lowerThirdShape: 'minimal',
    lowerThirdAnimation: 'none',
    // QR Code
    qrUrl: '',
    showQrCode: false,
    qrPosition: 'bottom-right',
    qrColor: '#001233',
    qrBgColor: '#ffffff',
    qrSize: 'medium',
    qrLogo: '',
  });

  const useRaisedHandParticipants = () => {
    const raisedHandsParticipantsRef = useRef();

    // Legacy toggle — kept for backwards compatibility with publishers that
    // send the old "Raise Hand" literal string. NEW publishers must instead
    // send {action: "raise" | "lower"} and the receiver must call
    // setParticipantHandRaised() below, which is idempotent (safe under
    // redelivery / dropped messages and doesn't desync across participants).
    const participantRaisedHand = (participantId) => {
      const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];

      const participantFound = raisedHandsParticipants.findIndex(
        ({ participantId: pID }) => pID === participantId
      );

      let action;
      if (participantFound === -1) {
        // Participant is raising their hand
        const newItem = { participantId, raisedHandOn: new Date().getTime() };
        raisedHandsParticipants.push(newItem);
        action = "raised";
      } else {
        // Participant is lowering their hand (toggle off)
        raisedHandsParticipants.splice(participantFound, 1);
        action = "lowered";
      }

      setRaisedHandsParticipants(raisedHandsParticipants);
      return action;
    };

    // Idempotent absolute setter — used by the new {action: "raise"|"lower"}
    // payload. Two redeliveries of the same message no longer flip state, so
    // a dropped or out-of-order RTM message can't leave one participant's
    // view desynced from the rest of the room.
    const setParticipantHandRaised = (participantId, raised) => {
      const current = [...(raisedHandsParticipantsRef.current || [])];
      const idx = current.findIndex(
        ({ participantId: pID }) => pID === participantId
      );
      const alreadyRaised = idx !== -1;
      if (raised && !alreadyRaised) {
        current.push({ participantId, raisedHandOn: new Date().getTime() });
      } else if (!raised && alreadyRaised) {
        current.splice(idx, 1);
      } else {
        // Already in the target state — no-op
        return raised ? "raised" : "lowered";
      }
      setRaisedHandsParticipants(current);
      return raised ? "raised" : "lowered";
    };

    useEffect(() => {
      raisedHandsParticipantsRef.current = raisedHandsParticipants;
    }, [raisedHandsParticipants]);

    // AUTO-CLEANUP DISABLED: Raised hands now persist until manually lowered.
    // Uncomment the code below to re-enable auto-cleanup after 15 seconds.
    // const _handleRemoveOld = () => {
    //   const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];
    //
    //   const now = new Date().getTime();
    //
    //   const persisted = raisedHandsParticipants.filter(({ raisedHandOn }) => {
    //     return parseInt(raisedHandOn) + 15000 > parseInt(now);
    //   });
    //
    //   if (raisedHandsParticipants.length !== persisted.length) {
    //     setRaisedHandsParticipants(persisted);
    //   }
    // };
    //
    // useEffect(() => {
    //   const interval = setInterval(_handleRemoveOld, 1000);
    //
    //   return () => {
    //     clearInterval(interval);
    //   };
    // }, []);

    return { participantRaisedHand, setParticipantHandRaised };
  };

  return (
    <MeetingAppContext.Provider
      value={{
        // states

        raisedHandsParticipants,
        brbParticipants,
        selectedMic,
        selectedWebcam,
        selectedSpeaker,
        sideBarMode,
        pipMode,
        isCameraPermissionAllowed,
        isMicrophonePermissionAllowed,

        // setters

        setRaisedHandsParticipants,
        setBrbParticipants,
        setSelectedMic,
        setSelectedWebcam,
        setSelectedSpeaker,
        setSideBarMode,
        setPipMode,
        useRaisedHandParticipants,
        setIsCameraPermissionAllowed,
        setIsMicrophonePermissionAllowed,
        setUnreadChat,
        unreadChat,
        setRecentReactions,
        recentReactions,
        participantReactions,
        setParticipantReactions,
        selectedLanguage,
        setSelectedLanguage,
        setTranslationEnabled,
        translationEnabled,
        setTranscriptionEnabled,
        transcriptionEnabled,
        virtualBackgroundOn,
        setVirtualBackgroundOn,
        blurEnabled,
        setBlurEnabled,
        blurStrength,
        setBlurStrength,
        selectedBackground,
        setSelectedBackground,
        participantApproveFunc,
        setParticipantApproveFunc,
        approvalSoundEnabled,
        setApprovalSoundEnabled,
        afterMeetingJoinedHLSState,
        setAfterMeetingJoinedHLSState,
        participantWebinarFunc,
        setParticipantWebinarFunc,
        activeSpeakerId,
        setActiveSpeakerId,
        speakingTimes,
        setSpeakingTimes,
        currentFocusParticipantId,
        setCurrentFocusParticipantId,
        showRecordingConsentModal,
        setShowRecordingConsentModal,
        isParticipantRowMinimized,
        setIsParticipantRowMinimized,
        shadowBannedParticipants,
        setShadowBannedParticipants,
        presenters,
        setPresenters,
        recordingStartedById,
        setRecordingStartedById,
        gridPagination,
        setGridPagination,
        branding,
        setBranding,
        participantBranding,
        setParticipantBranding,
      }}
    >
      {children}
    </MeetingAppContext.Provider>
  );
};
